// INVDESIGN1-S1 (docs/build-briefs/INVDESIGN1-S1-count-flow.md; design/style-guide.md; owner-approved mockups
// invdesign1-3-count-back-shelf-ipad and invdesign1-4-count-kegs-ipad). The area-by-area count: one area at a time in
// walking order, finished items collapsed to a row with Change, the active bottle with a full-bottle stepper and tenths
// buttons, kegs by fraction left on tap plus full spares.
//
// Draws from rows and callbacks it is handed and writes nothing itself: every tap goes back to countTable.ts, which
// saves through the same count routes as the table, so blind rules, the assigned-counter lock, review and close are the
// ones the table has. Touch only: no text input, every target at least --touch-target-min.

import { categoryLabel } from "./catalogDisplay.ts";
import {
  AREA_TENTHS,
  KEG_TAP_QUARTERS,
  MAX_KEG_SPARES,
  areaRows,
  areaTabs,
  countingFigure,
  followingAreaId,
  isKegRow,
  kegCountFromEntry,
  kegReading,
  kegSizeText,
  kegTapLabel,
  nextUncountedId,
  previousAreaId,
  tenthsLabel,
  type AreaTab,
  type KegCount,
} from "./countAreaModel.ts";
import {
  MAX_ENTRY_WHOLE,
  parText,
  setEntryTenths,
  stepEntry,
  zeroEntry,
  type TableEntry,
  type TableRow,
} from "./countTableModel.ts";

export interface AreaHost {
  rows: readonly TableRow[];
  sections: readonly { id: string; name: string }[];
  blind: boolean;
  /** This user holds the count. False locks every entry control. */
  mine: boolean;
  holderName: string;
  canTakeOver: boolean;
  canBlind: boolean;
  busy: boolean;
  areaId: string;
  activeId: string | undefined;
  showLast: boolean;
  /** The quarters on tap the counter picked, per keg, which the saved total alone cannot say. */
  kegTaps: ReadonlyMap<string, number>;
  onExit(): void;
  onReview(): void;
  onTakeOver(): void;
  onMenu(anchor: HTMLElement): void;
  onSelectArea(areaId: string): void;
  onActivate(itemId: string | undefined): void;
  onToggleLast(): void;
  onEntry(row: TableRow, entry: TableEntry): void;
  onKeg(row: TableRow, count: KegCount): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(
  className: string,
  label: string,
  testId: string,
  onTap: () => void,
  ariaLabel?: string,
): HTMLButtonElement {
  const node = el("button", className, label);
  node.type = "button";
  node.dataset["testid"] = testId;
  if (ariaLabel !== undefined) node.setAttribute("aria-label", ariaLabel);
  node.addEventListener("click", onTap);
  return node;
}

function progressBar(counted: number, total: number): HTMLElement {
  const track = el("div", "ca-meter");
  track.setAttribute("aria-hidden", "true");
  const fill = el("div", "ca-meter-fill");
  fill.style.width = total === 0 ? "0%" : `${Math.round((counted / total) * 100)}%`;
  track.append(fill);
  return track;
}

/** "750 ml bottle" and the last count: the one muted line under an item's name. */
function subLine(host: AreaHost, row: TableRow, active: boolean): string {
  const parts: string[] = [];
  const base = isKegRow(row)
    ? (kegSizeText(row.kegSize) ?? row.sizeText)
    : (row.sizeText ?? categoryLabel(row.category));
  if (base !== null) parts.push(base);
  if (host.showLast && !host.blind && row.lastBottles !== undefined && row.lastBottles !== null) {
    parts.push(`Last ${row.lastBottles}`);
  }
  // INVPOLISH1: the item being counted says what the location keeps on hand for it. A target, so a blind count shows it too.
  const par = active ? parText(row) : null;
  if (par !== null) parts.push(par);
  return parts.join(" · ");
}

export function buildAreaScreen(host: AreaHost): HTMLElement {
  const tabs = areaTabs(host.rows, host.sections);
  const active = tabs.find((tab) => tab.id === host.areaId) ?? tabs[0];
  const screen = el("div", "ct-screen ca-screen");
  screen.dataset["testid"] = "count-area-screen";
  screen.append(buildTop(host, active), buildTabs(host, tabs));
  if (!host.mine) screen.append(buildHolder(host));
  screen.append(buildList(host, active), buildFoot(host, tabs, active));
  return screen;
}

function isKegArea(host: AreaHost, area: AreaTab | undefined): boolean {
  if (area === undefined) return false;
  const inArea = areaRows(host.rows, area.id);
  return inArea.length > 0 && inArea.every(isKegRow);
}

function buildTop(host: AreaHost, area: AreaTab | undefined): HTMLElement {
  const top = el("header", "ca-top");
  top.append(button("ca-exit", "‹ Save and exit", "count-area-exit", host.onExit));
  const title = el("h2", "ca-title", `Count: ${area?.label ?? "Items"}`);
  title.dataset["testid"] = "count-area-title";
  top.append(title, el("div", "ca-spacer"));

  const counted = host.rows.filter((row) => row.entry !== null).length;
  const progress = el("div", "ca-progress");
  const text = el("div", "ca-progress-text");
  text.dataset["testid"] = "count-area-progress";
  text.setAttribute("aria-live", "polite");
  text.append(
    el("strong", undefined, String(counted)),
    el("span", undefined, ` of ${host.rows.length} counted`),
  );
  progress.append(text, progressBar(counted, host.rows.length));
  top.append(progress);

  if (!host.blind && !isKegArea(host, area)) {
    const last = button(
      "ca-btn",
      `Show last count: ${host.showLast ? "On" : "Off"}`,
      "count-area-last-toggle",
      host.onToggleLast,
    );
    last.setAttribute("aria-pressed", String(host.showLast));
    top.append(last);
  }
  const more = el("button", "ca-more");
  more.type = "button";
  more.textContent = "⋯";
  more.dataset["testid"] = "count-area-more";
  more.setAttribute("aria-label", "More");
  more.addEventListener("click", () => host.onMenu(more));
  top.append(more);
  return top;
}

function buildHolder(host: AreaHost): HTMLElement {
  const strip = el("div", "ca-holder");
  const who = el("span", undefined, `${host.holderName} is counting this.`);
  who.dataset["testid"] = "count-area-holder";
  strip.append(who);
  if (host.canTakeOver) {
    strip.append(
      button("ca-btn ca-btn-primary", "Take over", "count-area-takeover", host.onTakeOver),
    );
  }
  return strip;
}

function buildTabs(host: AreaHost, tabs: readonly AreaTab[]): HTMLElement {
  const nav = el("nav", "ca-tabs");
  nav.setAttribute("aria-label", "Areas");
  for (const tab of tabs) {
    const selected = tab.id === host.areaId;
    const node = el("button", `ca-tab${selected ? " is-active" : ""}${tab.done ? " is-done" : ""}`);
    node.type = "button";
    node.dataset["testid"] = `count-area-tab-${tab.id}`;
    node.setAttribute("aria-pressed", String(selected));
    node.append(el("span", "ca-tab-name", `${tab.done ? "✓ " : ""}${tab.label}`));
    const count = el(
      "span",
      "ca-tab-count",
      tab.total === 0 ? "-" : `${tab.counted} of ${tab.total}`,
    );
    count.dataset["testid"] = `count-area-tab-count-${tab.id}`;
    node.append(count);
    node.addEventListener("click", () => host.onSelectArea(tab.id));
    nav.append(node);
  }
  return nav;
}

function buildList(host: AreaHost, area: AreaTab | undefined): HTMLElement {
  const list = el("main", "ca-list");
  list.dataset["testid"] = "count-area-list";
  const rows = area === undefined ? [] : areaRows(host.rows, area.id);
  if (rows.length === 0) {
    const empty = el("p", "ca-empty", "Nothing is set to be counted here. Pick another area.");
    empty.dataset["testid"] = "count-area-empty";
    list.append(empty);
    return list;
  }
  rows.forEach((row, index) => {
    const isActive = host.activeId === row.id;
    list.append(
      isKegRow(row)
        ? buildKegRow(host, row, index, isActive)
        : buildBottleRow(host, row, index, isActive),
    );
    if (isKegRow(row) && isActive && row.entry !== null) {
      const note = el("p", "ca-note", kegReading(row.name, currentKeg(host, row)));
      note.dataset["testid"] = "count-area-keg-reading";
      list.append(note);
    }
  });
  return list;
}

function marker(counted: boolean, isActive: boolean, index: number): HTMLElement {
  if (isActive) {
    const badge = el("span", "ca-badge", String(index + 1));
    badge.dataset["testid"] = "count-area-active-badge";
    return badge;
  }
  const node = el("span", `ca-check${counted ? " is-counted" : ""}`, counted ? "✓" : "·");
  node.dataset["testid"] = "count-area-counted";
  node.dataset["counted"] = String(counted);
  node.setAttribute("role", "img");
  node.setAttribute("aria-label", counted ? "Counted" : "Not counted");
  return node;
}

function rowShell(row: TableRow, className: string): HTMLElement {
  const node = el("div", className);
  node.dataset["testid"] = "count-area-row";
  node.dataset["itemId"] = row.id;
  node.dataset["itemName"] = row.name;
  node.dataset["counted"] = String(row.entry !== null);
  return node;
}

function itemBlock(host: AreaHost, row: TableRow, active = false): HTMLElement {
  const item = el("div", "ca-item");
  const name = el("strong", "ca-name", row.name);
  name.dataset["testid"] = "count-area-name";
  const sub = el("span", "ca-sub", subLine(host, row, active));
  sub.dataset["testid"] = "count-area-sub";
  item.append(name, sub);
  return item;
}

// ---------------------------------------------------------------------
// Bottles
// ---------------------------------------------------------------------

function buildBottleRow(
  host: AreaHost,
  row: TableRow,
  index: number,
  isActive: boolean,
): HTMLElement {
  const counted = row.entry !== null;
  if (isActive) return buildActiveBottle(host, row, index);
  if (!counted) {
    const pending = rowShell(row, "ca-row is-pending");
    pending.append(marker(false, false, index), itemBlock(host, row));
    const tap = button("ca-tap", "Tap to count", "count-area-tap-to-count", () =>
      host.onActivate(row.id),
    );
    tap.setAttribute("aria-label", `Count ${row.name}`);
    pending.append(tap);
    return pending;
  }
  const done = rowShell(row, "ca-row is-done");
  const value = el("strong", "ca-value", countingFigure(row.entry, row.caseSize));
  value.dataset["testid"] = "count-area-value";
  done.append(
    marker(true, false, index),
    itemBlock(host, row),
    value,
    button(
      "ca-btn ca-change",
      "Change",
      "count-area-change",
      () => host.onActivate(row.id),
      `Change ${row.name}`,
    ),
  );
  return done;
}

function stepperBox(
  host: AreaHost,
  row: TableRow,
  field: "cases" | "bottles",
  label: string,
): HTMLElement {
  const box = el("div", "ca-field");
  box.append(el("span", "ca-label", label));
  const stepper = el("div", "ca-stepper");
  stepper.dataset["testid"] = `count-area-${field}`;
  const value = row.entry === null ? 0 : row.entry[field];
  const minus = button(
    "ca-step",
    "−",
    `count-area-${field}-minus`,
    () => host.onEntry(row, stepEntry(row.entry, field, -1)),
    `Fewer ${field}, ${row.name}`,
  );
  minus.disabled = !host.mine || value === 0;
  const shown = el("span", "ca-step-value", String(value));
  shown.dataset["testid"] = `count-area-${field}-value`;
  const plus = button(
    "ca-step ca-step-plus",
    "+",
    `count-area-${field}-plus`,
    () => host.onEntry(row, stepEntry(row.entry, field, 1)),
    `More ${field}, ${row.name}`,
  );
  plus.disabled = !host.mine || value >= MAX_ENTRY_WHOLE;
  stepper.append(minus, shown, plus);
  box.append(stepper);
  return box;
}

function buildActiveBottle(host: AreaHost, row: TableRow, index: number): HTMLElement {
  const card = rowShell(row, "ca-active");
  card.dataset["active"] = "true";
  const head = el("div", "ca-active-head");
  head.append(marker(false, true, index), itemBlock(host, row, true));
  head.lastElementChild?.classList.add("ca-grow");
  const figure = el("div", "ca-figure");
  figure.append(el("span", "ca-label", "Counting"));
  const number = el("strong", undefined, countingFigure(row.entry, row.caseSize));
  number.dataset["testid"] = "count-area-figure";
  figure.append(number);
  head.append(figure);

  const controls = el("div", "ca-controls");
  if (row.caseSize !== null) {
    controls.append(stepperBox(host, row, "cases", `Cases (of ${row.caseSize})`));
  }
  controls.append(stepperBox(host, row, "bottles", "Full bottles"));
  const part = el("div", "ca-field ca-grow");
  part.append(el("span", "ca-label", "Part bottle, in tenths (tap how full the open one is)"));
  const grid = el("div", "ca-tenths");
  grid.dataset["testid"] = "count-area-tenths";
  const chosen = row.entry === null ? undefined : row.entry.tenths;
  for (const tenth of AREA_TENTHS) {
    const pick = button(
      `ca-tenth${chosen === tenth ? " is-on" : ""}`,
      tenthsLabel(tenth),
      `count-area-tenth-${tenth}`,
      () => host.onEntry(row, setEntryTenths(row.entry, tenth)),
      tenth === 0 ? `No part bottle, ${row.name}` : `${tenth} tenths, ${row.name}`,
    );
    pick.setAttribute("aria-pressed", String(chosen === tenth));
    pick.disabled = !host.mine;
    grid.append(pick);
  }
  part.append(grid);
  controls.append(part);
  card.append(head, controls);
  return card;
}

// ---------------------------------------------------------------------
// Kegs
// ---------------------------------------------------------------------

/** What the keg row shows now: the counter's own pick when there is one, else read back from the saved total. */
function currentKeg(host: AreaHost, row: TableRow): KegCount {
  if (row.entry === null) return { tapQuarters: 0, spares: 0 };
  const fromTotal = kegCountFromEntry(row.entry);
  const picked = host.kegTaps.get(row.id);
  if (picked === undefined) return fromTotal;
  const quarters = fromTotal.tapQuarters + fromTotal.spares * 4;
  return { tapQuarters: picked, spares: Math.max(0, Math.floor((quarters - picked) / 4)) };
}

function buildKegRow(host: AreaHost, row: TableRow, index: number, isActive: boolean): HTMLElement {
  const counted = row.entry !== null;
  const node = rowShell(
    row,
    `ca-row ca-keg${isActive ? " is-active" : ""}${counted ? " is-done" : ""}`,
  );
  if (isActive) node.dataset["active"] = "true";
  node.append(marker(counted, isActive, index), itemBlock(host, row, isActive));

  const keg = currentKeg(host, row);
  const tap = el("div", "ca-field ca-keg-tap");
  tap.append(el("span", "ca-label", "On tap, how much is left"));
  const choices = el("div", "ca-keg-choices");
  choices.dataset["testid"] = "count-area-keg-choices";
  for (const quarters of KEG_TAP_QUARTERS) {
    const on = counted && keg.tapQuarters === quarters;
    const pick = button(
      `ca-keg-pick${on ? " is-on" : ""}`,
      kegTapLabel(quarters),
      `count-area-keg-tap-${quarters}`,
      () => host.onKeg(row, { tapQuarters: quarters, spares: keg.spares }),
      `${kegTapLabel(quarters)} left on tap, ${row.name}`,
    );
    pick.setAttribute("aria-pressed", String(on));
    pick.disabled = !host.mine;
    choices.append(pick);
  }
  tap.append(choices);

  const spares = el("div", "ca-field ca-keg-spares");
  spares.append(el("span", "ca-label", "Full spares"));
  const stepper = el("div", "ca-stepper");
  stepper.dataset["testid"] = "count-area-spares";
  const minus = button(
    "ca-step",
    "−",
    "count-area-spares-minus",
    () => host.onKeg(row, { ...keg, spares: keg.spares - 1 }),
    `Fewer full spares, ${row.name}`,
  );
  minus.disabled = !host.mine || keg.spares === 0;
  const shown = el("span", "ca-step-value", String(keg.spares));
  shown.dataset["testid"] = "count-area-spares-value";
  const plus = button(
    "ca-step ca-step-plus",
    "+",
    "count-area-spares-plus",
    () => host.onKeg(row, { ...keg, spares: keg.spares + 1 }),
    `More full spares, ${row.name}`,
  );
  plus.disabled = !host.mine || keg.spares >= MAX_KEG_SPARES;
  stepper.append(minus, shown, plus);
  spares.append(stepper);

  node.append(tap, spares);
  return node;
}

// ---------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------

function buildFoot(
  host: AreaHost,
  tabs: readonly AreaTab[],
  area: AreaTab | undefined,
): HTMLElement {
  const foot = el("footer", "ca-foot");
  const rows = area === undefined ? [] : areaRows(host.rows, area.id);
  const activeRow = rows.find((row) => row.id === host.activeId);
  if (activeRow !== undefined && !isKegRow(activeRow)) {
    const missing = button(
      "ca-btn ca-btn-big",
      "Not on the shelf",
      "count-area-not-on-shelf",
      () => {
        host.onEntry(activeRow, zeroEntry());
        host.onActivate(nextUncountedId(rows, activeRow.id));
      },
    );
    missing.disabled = !host.mine;
    const hint = el("span", "ca-hint", "Mark an item as 0 or skip it. You can come back.");
    const next = button(
      "ca-btn ca-btn-primary ca-btn-big",
      "Next item",
      "count-area-next-item",
      () => host.onActivate(nextUncountedId(rows, activeRow.id)),
    );
    foot.append(missing, hint, el("div", "ca-spacer"), next);
    return foot;
  }
  const before = area === undefined ? undefined : previousAreaId(tabs, area.id);
  const after = area === undefined ? undefined : followingAreaId(tabs, area.id);
  const beforeLabel = tabs.find((tab) => tab.id === before)?.label;
  const afterLabel = tabs.find((tab) => tab.id === after)?.label;
  if (before !== undefined && beforeLabel !== undefined) {
    foot.append(
      button("ca-btn ca-btn-big", `‹ ${beforeLabel}`, "count-area-prev-area", () =>
        host.onSelectArea(before),
      ),
    );
  }
  foot.append(el("div", "ca-spacer"));
  if (after !== undefined && afterLabel !== undefined) {
    foot.append(
      button(
        "ca-btn ca-btn-primary ca-btn-big",
        `Next: ${afterLabel}`,
        "count-area-next-area",
        () => host.onSelectArea(after),
      ),
    );
  } else {
    const review = button(
      "ca-btn ca-btn-primary ca-btn-big",
      "Review",
      "count-area-review",
      host.onReview,
    );
    review.disabled = host.busy;
    foot.append(review);
  }
  return foot;
}
